import { createFrontOfficeInstance } from '@/shared/api/api-instance';

export type Kpi = {
  key: string;
  titleKey: string;
  value: number | null;
  suffixKey?: 'minutes' | 'percent';
  tone: 'primary' | 'success' | 'warning' | 'violet';
};

export type ChannelMetric = {
  id: string;
  name: string;
  value: number;
  share: number;
  color: string;
};

export type DailyMetric = {
  dayKey: string;
  inbound: number;
  outbound: number;
};

export type AnalyticsReport = {
  range: { from: string; to: string; days: number };
  kpi: {
    dialogs: number;
    firstResponseMinutes: number | null;
    conversionPercent: number | null;
    missed: number;
  };
  workload: DailyMetric[];
  channels: ChannelMetric[];
  funnel: {
    incoming: number;
    answered: number;
    qualified: number;
    dealCreated: number | null;
  };
  recommendations: string[];
};

export type AiAnalyticsDataStatus = 'ready' | 'partial' | 'unavailable';
export type AiAnalyticsReport = {
  company_id: string;
  range: { from: string; to: string; days: number };
  data_status: {
    activity_logs: AiAnalyticsDataStatus;
    delayed_deliveries: AiAnalyticsDataStatus;
    module_scope: AiAnalyticsDataStatus;
    outbound_policy: AiAnalyticsDataStatus;
    chat_metrics: AiAnalyticsDataStatus;
    crm_attribution: AiAnalyticsDataStatus;
    channel_health: AiAnalyticsDataStatus;
  };
  module_scope: {
    analytics_status:
      | 'enabled'
      | 'disabled'
      | 'billing_required'
      | 'misconfigured';
    billing_state: 'included' | 'unavailable';
    scoped_channel_ids: string[];
    capabilities: string[];
    setup_state: 'ready' | 'needs_channels' | 'coming_soon';
  };
  activity: {
    draft_replies: number;
    campaign_drafts: number;
    autonomous_replies_sent: number;
    handoff_pauses: number;
    safe_automation_blocks: number;
    failed_operations: number;
  };
  deliveries: {
    delayed_pending: number;
    delayed_processing: number;
    delayed_sent: number;
    delayed_blocked: number;
    delayed_failed: number;
    retry_count: number;
  };
  automation: {
    allowed: number;
    blocked: number;
    dry_run: number;
    by_source: Array<{
      source: string;
      allowed: number;
      blocked: number;
      dry_run: number;
    }>;
    by_reason: Array<{
      reason: string;
      count: number;
    }>;
    anti_spam_queue_health: {
      delay_required: number;
      daily_limit_reached: number;
      failed: number;
      retry_pressure: number;
    };
  };
  provider: {
    avg_latency_ms: number | null;
    p95_latency_ms: number | null;
    total_tokens: number;
    estimated_cost_usd: number | null;
    retry_count: number;
    by_model: Array<{
      provider: string;
      model: string;
      calls: number;
      failures: number;
      avg_latency_ms: number | null;
      p95_latency_ms: number | null;
      total_tokens: number;
      estimated_cost_usd: number | null;
    }>;
  };
  operational: {
    first_response_minutes: number | null;
    missed_dialogs: number;
    lost_leads_count: number;
    answered_dialogs: number;
    inbound_dialogs: number;
    ai_containment_rate: number | null;
  };
  channels: Array<{
    channel_id: string;
    name: string | null;
    protocol: string | null;
    status: string | null;
    outbound_messages: number;
    auth_issue: boolean;
    billing_issue: boolean;
    last_error: string | null;
  }>;
  risk: {
    low: number;
    medium: number;
    high: number;
    critical: number;
    top_risky_dialogs: Array<{
      channel_id: string;
      chat_id: string;
      level: 'low' | 'medium' | 'high' | 'critical';
      score: number;
      reason: string;
      signals: string[];
      handoff_reason: string | null;
      updated_at: string;
      crm_deal_id: string | null;
      crm_deal_amount: number | null;
    }>;
  };
  lost_leads: Array<{
    channel_id: string;
    chat_id: string;
    contact_id: string | null;
    first_inbound_at: string;
    first_response_at: string | null;
    minutes_to_response: number | null;
    crm_deal_id: string | null;
    crm_deal_amount: number | null;
  }>;
  crm_impact: {
    attribution_ready: boolean;
    attributed_dialogs: number;
    unattributed_dialogs: number;
    risky_deal_amount: AiAnalyticsMoneyMetric;
    lost_deal_amount: AiAnalyticsMoneyMetric;
    saved_deal_amount: AiAnalyticsMoneyMetric;
  };
};

export type AiAnalyticsMoneyMetric = {
  amount: number | null;
  currency?: string;
};

export type FetchAiAnalyticsReportInput = {
  companyId: string;
  range: { from: string; to: string };
  lostLeadSlaMinutes?: number;
};

export const defaultLostLeadSlaMinutes = 15;

export const formatNumber = (value: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value);

export const formatMetricValue = (value: number | null) =>
  value === null ? '—' : formatNumber(value);

export const formatPercentValue = (value: number | null) =>
  value === null ? '—' : `${formatNumber(value)}%`;

export const formatDurationMs = (value: number | null) => {
  if (value === null) return '—';

  return value >= 1000
    ? `${formatNumber(value / 1000)} s`
    : `${formatNumber(value)} ms`;
};

export const formatCurrencyValue = (
  amount: number | null,
  currency = 'RUB'
) => {
  if (amount === null) return '—';

  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${formatNumber(amount)} ${currency}`;
  }
};

export const fetchAnalyticsReport = (range: { from: string; to: string }) =>
  createFrontOfficeInstance<AnalyticsReport>({
    url: '/api/v1/analytics',
    method: 'GET',
    params: range,
  });

export const fetchAiAnalyticsReport = ({
  companyId,
  range,
  lostLeadSlaMinutes = defaultLostLeadSlaMinutes,
}: FetchAiAnalyticsReportInput) =>
  createFrontOfficeInstance<AiAnalyticsReport>({
    url: '/api/v1/ai/analytics/report',
    method: 'GET',
    params: {
      company_id: companyId,
      from: range.from,
      to: range.to,
      lost_lead_sla_minutes: lostLeadSlaMinutes,
    },
  });

export const isAnalyticsReport = (value: unknown): value is AnalyticsReport => {
  if (value === null || typeof value !== 'object') return false;
  const report = value as Partial<AnalyticsReport>;
  return Boolean(
    report.kpi &&
      Array.isArray(report.workload) &&
      Array.isArray(report.channels) &&
      report.funnel &&
      Array.isArray(report.recommendations)
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isNullableNumber = (value: unknown): value is number | null =>
  value === null || isNumber(value);

const hasNumberFields = (value: unknown, keys: string[]) =>
  isRecord(value) && keys.every((key) => isNumber(value[key]));

const hasNullableNumberFields = (value: unknown, keys: string[]) =>
  isRecord(value) && keys.every((key) => isNullableNumber(value[key]));

const dataStatusValues: AiAnalyticsDataStatus[] = [
  'ready',
  'partial',
  'unavailable',
];

const hasDataStatuses = (value: unknown) =>
  isRecord(value) &&
  [
    'activity_logs',
    'delayed_deliveries',
    'module_scope',
    'outbound_policy',
    'chat_metrics',
    'crm_attribution',
    'channel_health',
  ].every((key) =>
    dataStatusValues.includes(value[key] as AiAnalyticsDataStatus)
  );

const isMoneyMetric = (value: unknown): value is AiAnalyticsMoneyMetric =>
  isRecord(value) &&
  isNullableNumber(value.amount) &&
  (value.currency === undefined || typeof value.currency === 'string');

export const isAiAnalyticsReport = (
  value: unknown
): value is AiAnalyticsReport => {
  if (!isRecord(value)) return false;

  const moduleScope = value.module_scope;
  const automation = value.automation;
  const provider = value.provider;
  const operational = value.operational;
  const risk = value.risk;
  const crmImpact = value.crm_impact;

  return (
    typeof value.company_id === 'string' &&
    isRecord(value.range) &&
    typeof value.range.from === 'string' &&
    typeof value.range.to === 'string' &&
    isNumber(value.range.days) &&
    hasDataStatuses(value.data_status) &&
    isRecord(moduleScope) &&
    typeof moduleScope.analytics_status === 'string' &&
    typeof moduleScope.billing_state === 'string' &&
    Array.isArray(moduleScope.scoped_channel_ids) &&
    Array.isArray(moduleScope.capabilities) &&
    typeof moduleScope.setup_state === 'string' &&
    hasNumberFields(value.activity, [
      'draft_replies',
      'campaign_drafts',
      'autonomous_replies_sent',
      'handoff_pauses',
      'safe_automation_blocks',
      'failed_operations',
    ]) &&
    hasNumberFields(value.deliveries, [
      'delayed_pending',
      'delayed_processing',
      'delayed_sent',
      'delayed_blocked',
      'delayed_failed',
      'retry_count',
    ]) &&
    isRecord(automation) &&
    hasNumberFields(automation, ['allowed', 'blocked', 'dry_run']) &&
    Array.isArray(automation.by_source) &&
    Array.isArray(automation.by_reason) &&
    hasNumberFields(automation.anti_spam_queue_health, [
      'delay_required',
      'daily_limit_reached',
      'failed',
      'retry_pressure',
    ]) &&
    isRecord(provider) &&
    hasNullableNumberFields(provider, [
      'avg_latency_ms',
      'p95_latency_ms',
      'estimated_cost_usd',
    ]) &&
    isNumber(provider.total_tokens) &&
    isNumber(provider.retry_count) &&
    Array.isArray(provider.by_model) &&
    isRecord(operational) &&
    hasNullableNumberFields(operational, [
      'first_response_minutes',
      'ai_containment_rate',
    ]) &&
    hasNumberFields(operational, [
      'missed_dialogs',
      'lost_leads_count',
      'answered_dialogs',
      'inbound_dialogs',
    ]) &&
    Array.isArray(value.channels) &&
    isRecord(risk) &&
    hasNumberFields(risk, ['low', 'medium', 'high', 'critical']) &&
    Array.isArray(risk.top_risky_dialogs) &&
    Array.isArray(value.lost_leads) &&
    isRecord(crmImpact) &&
    typeof crmImpact.attribution_ready === 'boolean' &&
    isNumber(crmImpact.attributed_dialogs) &&
    isNumber(crmImpact.unattributed_dialogs) &&
    isMoneyMetric(crmImpact.risky_deal_amount) &&
    isMoneyMetric(crmImpact.lost_deal_amount) &&
    isMoneyMetric(crmImpact.saved_deal_amount)
  );
};
